import React, { useEffect, useRef, useState } from "react";
import { DesplegableModel, Evolucion, Turno } from "../../interfaces";
import RtfEditor from "../RtfEditor/RtfEditor";
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonText,
  IonRow,
} from "@ionic/react";
import StyledButton from "../StyledButton/StyledButton";
import useNotificacion from "../../hooks/notificacion";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import parse from "html-react-parser";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import { useSession } from "../../hooks/SessionContext";

import http from "../../hooks/httpAdapter";
import { useHistory } from "react-router";
require("dayjs/locale/es");

dayjs.extend(customParseFormat);

interface ContainerProps {
  evolucionSel?: Evolucion;
  turnoEvo?: Turno;
  visualizar?: boolean;
  mostrarFecha?: boolean;
  setVisible: (visible: boolean) => void;
  setMostrarEditorEvolucion: (visible: boolean) => void;
  evolucionesDisponibles?: Evolucion[];
  handleClickChangeEvolucion: (sigPrev: "+" | "-") => void;
}
const EvolucionViewer: React.FC<ContainerProps> = ({
  evolucionSel,
  evolucionesDisponibles,
  handleClickChangeEvolucion,
  mostrarFecha = false,
  setMostrarEditorEvolucion,
}) => {
  const [diagnosticosSelect, setDiagnosticosSelect] = useState<
    DesplegableModel[]
  >([]);
  const [evolucion, setEvolucion] = useState<Evolucion>({
    codigo: 0,
    fecha: dayjs().format("YYYY-MM-DD"),
    prestadorCod: 0,
    prestadorNom: "",
    pacienteCod: 0,
    motivoConsulta: "",
    motivo: "",
    diagnosticoCod: 0,
    diagnosticoNom: "",
    derivanteMat: "",
    derivanteNom: "",
  });
  const [evolucionEditable, setEvolucionEditable] = useState<boolean>(false);
  const [disabledPrevEvolucion, setDisabledPrevEvolucion] = useState(true);
  const [disabledSigEvolucion, setDisabledSigEvolucion] = useState(true);
  const {
    hcUL,
    cargandoSession,
    pacienteCargado,
    setSesionInvalida,
    mostrarNotificacion,
  } = useSession();
  const history = useHistory();

  const TraerDiagnosticos = async () => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    try {
      const response = await http()
        .get(`Diagnosticos`, config)
        .catch((e) => {
          if (e.response && e.response.status === 401) {
            setSesionInvalida(true);
            // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
          } else {
            console.error(e);
          }
        });

      let listado: DesplegableModel[] = [];
      for (let i = 0; i < response.length; i++) {
        listado.push({
          codigo: response[i].codigo,
          text: response[i].nombre,
        });
      }
      setDiagnosticosSelect(listado);
    } catch (error) {
      //@ts-ignore
      mostrarNotificacion(true, "Error: " + error.response, "rojo");
      return [];
    }
  };

  const MostrarBotonEditar = () => {
    let fechaEvolucion = dayjs(evolucionSel?.fecha);
    let fechaLimite = dayjs().subtract(1, "day"); // Restar 1 día a la fecha actual

    return (
      evolucionSel?.prestadorCod === hcUL?.prestadorCod &&
      fechaEvolucion.isAfter(fechaLimite)
    );
  };

  const limpiarEvolucion = () => {
    setEvolucion({
      codigo: 0,
      fecha: dayjs().format("YYYY-MM-DD"),
      prestadorCod: hcUL?.prestadorCod || 0,
      prestadorNom: hcUL?.prestadorNom || "",
      pacienteCod: pacienteCargado?.codigo || 0,
      motivoConsulta: "",
      motivo: "",
      diagnosticoCod: 0,
      diagnosticoNom: "",
      derivanteMat: "",
      derivanteNom: "",
    });
  };

  useEffect(() => {
    if (cargandoSession === false) {
      if (pacienteCargado !== null && pacienteCargado.codigo !== 0) {
        limpiarEvolucion();
        TraerDiagnosticos();
        if (evolucionesDisponibles && evolucionesDisponibles.length > 0) {
          if (evolucionSel) setEvolucion(evolucionSel);
          const currentIndex = evolucionesDisponibles.findIndex(
            (evo) => evo === evolucionSel,
          );
          setDisabledPrevEvolucion(currentIndex === 0);
          setDisabledSigEvolucion(
            currentIndex === evolucionesDisponibles.length - 1,
          );
          setEvolucionEditable(MostrarBotonEditar);
        }
      } else {
        mostrarNotificacion(
          true,
          "Error: " + "No se encuentra al paciente",
          "rojo",
        );
      }
    }
  }, [
    evolucionSel,
    evolucionesDisponibles,
    hcUL,
    pacienteCargado,
    cargandoSession,
  ]);

  function parseBody(html: string): any {
    // Expresión regular para encontrar el contenido del cuerpo
    const bodyRegex = /<body[^>]*>((.|[\n\r])*)<\/body>/im;

    // Buscar el contenido del cuerpo usando la expresión regular
    const bodyMatches = html.match(bodyRegex);
    if (bodyMatches && bodyMatches.length > 1) {
      // Si se encuentra el contenido del cuerpo, devolverlo después de analizarlo
      const bodyContent = bodyMatches[1];
      return parse(bodyContent);
    } else {
      // Si no se encuentra el contenido del cuerpo, devolver null (o manejar el error según sea necesario)
      return null;
    }
  }

  return (
    <IonGrid>
      {evolucionesDisponibles !== undefined &&
        evolucionesDisponibles?.length > 0 && (
          <>
            <IonItem
              lines="none"
              className={
                mostrarFecha
                  ? "ion-align-items-start"
                  : "ion-justify-content-center ion-align-items-center"
              }
            >
              <IonButton
                slot="start"
                color="dark"
                onClick={() => handleClickChangeEvolucion("-")}
                disabled={disabledPrevEvolucion}
                expand="block"
                fill="clear"
              >
                <IonIcon icon={chevronBackOutline} size="small" />
              </IonButton>

              <IonText
                style={
                  mostrarFecha ? { flex: 1, textAlign: "left" } : undefined
                }
              >
                <IonRow>
                  {mostrarFecha && (
                    <IonCol size="12">
                      <div className="ion-text-start">
                        <p>
                          Fecha:{" "}
                          {evolucion.fecha
                            ? dayjs(evolucion.fecha).format("YYYY-MM-DD")
                            : "No registrada"}
                        </p>
                      </div>
                    </IonCol>
                  )}
                  <IonCol size="12">
                    <div className="ion-text-start">
                      <p>
                        Motivo:{evolucion.motivoConsulta || " No registrado"}
                      </p>
                    </div>
                  </IonCol>
                  <IonCol size="12">
                    <div className="ion-text-start">
                      <p>
                        Diagnostico:
                        {evolucion.diagnosticoNom || "No registrado"}
                      </p>
                    </div>
                  </IonCol>
                </IonRow>
              </IonText>

              <IonButton
                slot="end"
                color="dark"
                onClick={() => handleClickChangeEvolucion("+")}
                disabled={disabledSigEvolucion}
                expand="block"
                fill="clear"
              >
                <IonIcon icon={chevronForwardOutline} size="small" />
              </IonButton>
            </IonItem>
            <IonText>
              <IonRow>
                <IonCol size="12">
                  <div className="ion-text-start">
                    {parseBody(evolucion.motivo)}
                  </div>
                </IonCol>
              </IonRow>
            </IonText>
            {/* </IonItem> */}
            {evolucionEditable && (
              <IonRow>
                <IonCol>
                  <StyledButton
                    onClick={() => setMostrarEditorEvolucion(true)}
                    disabled={!evolucionEditable}
                    expand="block"
                  >
                    <IonText>Editar evolucion</IonText>
                    {/* <IonIcon icon={pencil} /> */}
                  </StyledButton>
                </IonCol>
              </IonRow>
            )}
          </>
        )}
    </IonGrid>
  );
};
export default EvolucionViewer;
